import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes } from 'react';
import { cn } from '@/shared/lib/cn';
import { ProcessingOverlay } from './processing-overlay';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/**
 * DialogContent: pure fade in/out. Centered via a flex wrapper rather than
 * `transform: translate(-50%, -50%)` because tailwindcss-animate's
 * `animate-in` keyframes rewrite the `transform` property — they'd clobber
 * the centering translate and the dialog would appear to slide from the
 * bottom-right. Clicks on the flex wrapper pass through (pointer-events-none)
 * so Radix's overlay still receives outside-clicks and can close the dialog.
 */
/**
 * `processing` prop wires up the app-wide "submit in flight" UX:
 *   - falsy → normal dialog
 *   - string → renders <ProcessingOverlay message={...}/> on top of the
 *     dialog AND blocks dismiss (Esc / backdrop click / Cancel button via
 *     `disabled`), so the user can't interact with form fields mid-write
 *     while the request is round-tripping.
 *
 * Used in place of the older in-button `<Loader2>` + `disabled={isPending}`
 * pattern. Consumers do `processing={mutation.isPending && 'Guardando…'}`
 * and drop the inline spinner from action buttons.
 */
export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean;
    processing?: false | string | null | undefined;
  }
>(({ className, children, hideClose = false, processing, onPointerDownOutside, onEscapeKeyDown, ...props }, ref) => {
  const isProcessing = typeof processing === 'string' && processing.length > 0;
  return (
    <DialogPortal>
      <DialogOverlay />
      <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center p-4">
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            'pointer-events-auto relative flex w-full max-w-2xl flex-col rounded-2xl border bg-card shadow-lg outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'max-h-[90vh] overflow-hidden',
            className,
          )}
          onPointerDownOutside={(e) => {
            if (isProcessing) e.preventDefault();
            onPointerDownOutside?.(e);
          }}
          onEscapeKeyDown={(e) => {
            if (isProcessing) e.preventDefault();
            onEscapeKeyDown?.(e);
          }}
          {...props}
        >
          {children}
          {!hideClose && !isProcessing && (
            <DialogPrimitive.Close className="absolute right-4 top-4 z-10 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <X className="h-5 w-5" aria-hidden />
              <span className="sr-only">Cerrar</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </div>
      {isProcessing && <ProcessingOverlay message={processing as string} />}
    </DialogPortal>
  );
});
DialogContent.displayName = 'DialogContent';

/** Fixed top of the dialog. Stays put while the body scrolls. */
export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('flex flex-col gap-1.5 px-6 pb-4 pt-6', className)} {...props} />
  );
}

/** Scrollable middle region. Native scrollbar is hidden. */
export function DialogBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('scrollbar-none flex-1 overflow-y-auto px-6 pb-4', className)}
      {...props}
    />
  );
}

/** Fixed bottom (actions row). Always visible. */
export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 border-t bg-card px-6 py-4 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-xl font-semibold tracking-tight text-foreground', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';
